// Atendimento → Grupos: grupos do WhatsApp (cadastrados na 1ª mensagem recebida) e menções ao número do suporte.
// Autorizar o grupo para transmissão de manuais, área e finalidade: grupos.gerenciar. Ver/tratar menções: grupos.ver.
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "react-toastify";
import {
  Button, Chip, MenuItem, Paper, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Tooltip, Typography,
} from "@material-ui/core";

import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import Title from "../../components/Title";
import suporteApi, { mensagemErro } from "../api";
import { dataBR, haQuanto } from "../comum";
import { usePermissao } from "../permissoes";
import SimuladorGrupo from "../SimuladorGrupo";

const AREAS = { "": "—", ALARME: "Suporte Alarme/CFTV", COFRES: "Suporte Cofres/Portas", CONTRATACAO: "Contratação",
  SUPERVISAO: "Supervisão de campo", DOCUMENTACAO: "Documentação" };
const SIT = { PENDENTE: "Pendente", EM_ATENDIMENTO: "Em atendimento", RESPONDIDA: "Respondida", IGNORADA: "Ignorada" };

const Grupos = () => {
  const [grupos, setGrupos] = useState([]);
  const [mencoes, setMencoes] = useState([]);
  const [filtroMencoes, setFiltroMencoes] = useState("ABERTAS");
  const podeGerenciar = usePermissao("grupos.gerenciar");

  const carregar = useCallback(() => {
    suporteApi.get("/api/grupos").then((r) => setGrupos(r.data)).catch((e) => toast.error(mensagemErro(e)));
    suporteApi.get("/api/grupos/mencoes", { params: { status: filtroMencoes } }).then((r) => setMencoes(r.data))
      .catch((e) => toast.error(mensagemErro(e)));
  }, [filtroMencoes]);
  useEffect(() => { carregar(); const t = setInterval(carregar, 30000); return () => clearInterval(t); }, [carregar]);

  const alterar = async (g, campos) => {
    try {
      await suporteApi.put(`/api/grupos/${g.contact_id}`, campos);
      carregar();
    } catch (e) {
      toast.error(mensagemErro(e));
    }
  };
  const tratar = async (m, status) => {
    try {
      await suporteApi.post(`/api/grupos/mencoes/${m.id}`, { status });
      carregar();
    } catch (e) {
      toast.error(mensagemErro(e));
    }
  };

  return (
    <MainContainer>
      <MainHeader><Title>Grupos do WhatsApp</Title></MainHeader>
      <SimuladorGrupo onMudou={carregar} />

      <Typography variant="subtitle1" style={{ marginTop: 4 }}><b>Menções ao suporte</b></Typography>
      <Typography variant="caption" color="textSecondary" component="div" style={{ marginBottom: 6 }}>
        Mensagens de grupo que marcaram o número do suporte (@). Responda no próprio grupo pela conversa e marque aqui.
      </Typography>
      <TextField id="mencoes-filtro" select size="small" variant="outlined" value={filtroMencoes} style={{ minWidth: 180, marginBottom: 8 }}
        onChange={(e) => setFiltroMencoes(e.target.value)}>
        <MenuItem value="ABERTAS">Em aberto</MenuItem>
        {Object.entries(SIT).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
      </TextField>
      <Paper variant="outlined" style={{ overflowX: "auto", marginBottom: 20 }}>
        <Table size="small">
          <TableHead><TableRow><TableCell>Quando</TableCell><TableCell>Grupo</TableCell><TableCell>Quem</TableCell>
            <TableCell>Mensagem</TableCell><TableCell>Situação</TableCell><TableCell /></TableRow></TableHead>
          <TableBody>
            {mencoes.length === 0 && <TableRow><TableCell colSpan={6}><Typography variant="body2" color="textSecondary">Nenhuma menção.</Typography></TableCell></TableRow>}
            {mencoes.map((m) => (
              <TableRow key={m.id}>
                <TableCell><Tooltip title={dataBR(m.criado_em)}><span>{haQuanto(m.criado_em)}</span></Tooltip></TableCell>
                <TableCell>{m.grupo || "—"}</TableCell>
                <TableCell>{m.autor_nome || "—"}</TableCell>
                <TableCell style={{ maxWidth: 420, whiteSpace: "pre-wrap" }}>{m.texto}</TableCell>
                <TableCell><Chip size="small" label={SIT[m.status] || m.status} />{m.via === "bot" ? " (bot)" : ""}</TableCell>
                <TableCell style={{ whiteSpace: "nowrap" }}>
                  {m.ticket_id && <Button size="small" href={`/tickets/${m.ticket_id}`}>Abrir conversa</Button>}
                  {["PENDENTE", "EM_ATENDIMENTO"].includes(m.status) && (
                    <>
                      <Button size="small" color="primary" onClick={() => tratar(m, "RESPONDIDA")}>Respondida</Button>
                      <Button size="small" onClick={() => tratar(m, "IGNORADA")}>Ignorar</Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>

      <Typography variant="subtitle1"><b>Grupos</b></Typography>
      <Typography variant="caption" color="textSecondary" component="div" style={{ marginBottom: 6 }}>
        Cada grupo aparece aqui na primeira mensagem recebida. Só os <b>autorizados</b> recebem manuais agendados.
      </Typography>
      <Paper variant="outlined" style={{ overflowX: "auto" }}>
        <Table size="small">
          <TableHead><TableRow><TableCell>Grupo</TableCell><TableCell>Área</TableCell><TableCell>Finalidade</TableCell>
            <TableCell>Transmissão</TableCell><TableCell>Ativo</TableCell><TableCell>Mensagens</TableCell><TableCell>Última</TableCell><TableCell>Menções abertas</TableCell></TableRow></TableHead>
          <TableBody>
            {grupos.length === 0 && <TableRow><TableCell colSpan={8}><Typography variant="body2" color="textSecondary">Nenhum grupo ainda.</Typography></TableCell></TableRow>}
            {grupos.map((g) => (
              <TableRow key={g.contact_id}>
                <TableCell>{g.nome || `grupo ${g.contact_id}`}</TableCell>
                <TableCell>
                  {podeGerenciar ? (
                    <TextField select size="small" value={g.area || ""} onChange={(e) => alterar(g, { area: e.target.value })} style={{ minWidth: 160 }}>
                      {Object.entries(AREAS).map(([k, v]) => <MenuItem key={k} value={k}>{v}</MenuItem>)}
                    </TextField>
                  ) : AREAS[g.area || ""]}
                </TableCell>
                <TableCell>
                  {podeGerenciar ? (
                    <TextField size="small" defaultValue={g.finalidade || ""} placeholder="ex.: técnicos de alarme"
                      onBlur={(e) => e.target.value !== (g.finalidade || "") && alterar(g, { finalidade: e.target.value })} />
                  ) : g.finalidade || "—"}
                </TableCell>
                <TableCell><Switch size="small" color="primary" checked={g.autorizado_transmissao} disabled={!podeGerenciar}
                  onChange={(e) => alterar(g, { autorizado_transmissao: e.target.checked })} /></TableCell>
                <TableCell><Switch size="small" checked={g.ativo} disabled={!podeGerenciar} onChange={(e) => alterar(g, { ativo: e.target.checked })} /></TableCell>
                <TableCell>{g.mensagens}</TableCell>
                <TableCell>{haQuanto(g.ultima_mensagem_em)}</TableCell>
                <TableCell>{g.mencoes_abertas ? <Chip size="small" color="secondary" label={g.mencoes_abertas} /> : 0}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Grupos;
